import { Component, inject, Input } from '@angular/core';
import { StructuredDataAbstractComponent } from "./structured-data.abstract.component";
import {
  StructuredDataBasicProductGroupInterface,
  StructuredDataBrandInterface,
  StructuredDataDefinedRegionInterface,
  StructuredDataProductGroupInterface,
  StructuredDataProductInterface,
  StructuredDataProductOfferInterface,
  StructuredDataProductPriceSpecificationInterface,
  StructuredDataProductShippingDetailsInterface,
  StructuredDataProductVariantInterface,
  StructuredDataReferenceInterface
} from "./models";
import {
  FlatProductBasicInterface,
  FlatProductItemInterface,
  KeyValueInterface,
  MatrixProduct,
  Product,
  ProductVariantInterface
} from "@gea-waldviertler-website/gea-models";
import { EnvService } from "@gea-waldviertler-website/shop-lib";

@Component({
  selector: 'gea-shop-product-structured-data',
  templateUrl: './structured-data.component.html',
  styleUrls: [],
  standalone: false
})
export class StructuredDataProductComponent extends StructuredDataAbstractComponent {
  @Input({required: true}) product?: Product<ProductVariantInterface>
  private envService: EnvService = inject(EnvService)

  // The shipping details are the same for nearly every variant of a product. Instead of repeating
  // them inside each single offer, every distinct set is emitted once as its own top level node
  // and the offers only reference it by @id. Both are rebuilt on every render.
  private shippingDetails: StructuredDataProductShippingDetailsInterface[] = []
  private shippingDetailIds: Map<string, string> = new Map()

  constructor(
  ) {
    super()
  }

  generateJsonLd() {
    if (! this.product) {
      return undefined
    }

    this.shippingDetails = []
    this.shippingDetailIds.clear()

    let product: StructuredDataProductGroupInterface | StructuredDataProductInterface | undefined

    // render shoes
    if (this.product.isShoe()) {
      product = this.generateShoeJsonLd(this.product as MatrixProduct)
      // render bags
    } else if (this.product.isBag()) {
      product = this.generateBagJsonLd(this.product as MatrixProduct)
    } else if (
      // Matrixproduct with one variant and no variable price
      this.product.typeId == 3
      && this.product.variants.length == 1
      && !this.product.variants[0].variablePrice
    ) {
      product = this.generateOneVariantProductJsonLd(this.product, this.product.variants[0])
    }

    if (! product) return undefined

    // the shipping details come first, so their @ids are defined before they are referenced
    return [ ... this.shippingDetails, product ]
  }

  private generateOneVariantProductJsonLd(
    product: Product<ProductVariantInterface>,
    variant: ProductVariantInterface
  ): StructuredDataProductInterface | undefined {

    // matrix products need a child to resolve price, stock and delivery time,
    // so pass the first child the same way getFlatItems() does
    const child = (variant.children && variant.children.length) ? variant.children[0] : undefined
    const flatProduct = product.getFlatVariantItem(variant, undefined, undefined, child)
    if (! flatProduct) return undefined

    return {
      "@context": "https://schema.org",
      "@type": "Product",
      category: flatProduct.category,
      mpn: flatProduct.id,
      name: flatProduct.name,
      sku: flatProduct.id,
      image: this.renderImageUrl(flatProduct.image),
      description: flatProduct.description,
      brand: this.generateBrandJsonLd(flatProduct.brand),
      ... (flatProduct.material ? { material: flatProduct.material } : {}),
      offers: this.generateProductOffersJsonLd(flatProduct)
    }
  }

  private generateBasicProductGroup(product: Product<ProductVariantInterface>): StructuredDataBasicProductGroupInterface {
    const flatProduct: FlatProductBasicInterface = product.getFlatInfo()
    return {
      "@context": "https://schema.org",
      "@type": "ProductGroup",
      "name": flatProduct.name,
      "description": flatProduct.description,
      "brand": this.generateBrandJsonLd(flatProduct.brand),
      "category": flatProduct.category,
      "productGroupID": flatProduct.mainId
    }
  }

  private generateBagJsonLd(product: MatrixProduct): StructuredDataProductGroupInterface {
    let data = { ... this.generateBasicProductGroup(product),
      ... {
        "variesBy": [
          "https://schema.org/color",
        ],
        url: "https://gea-waldviertler.at/shop/" + product.slug + '.html',
        "hasVariant": this.generateVariantsJsonLd(
          product, {'farbe': 'color'}
        )
      }} as StructuredDataProductGroupInterface

    return data
  }

  private generateShoeJsonLd(product: MatrixProduct): StructuredDataProductGroupInterface {
    let data = { ... this.generateBasicProductGroup(product), ... {
        "material": "Leder",
        "variesBy": [
          "https://schema.org/color",
          "https://schema.org/size",
        ],
        url: "https://gea-waldviertler.at/shop/" + product.slug + '.html',
        hasVariant: this.generateVariantsJsonLd(
          product, {'farbe': 'color'}, { 'schuhgroesse': 'size'},
        ),
      }} as StructuredDataProductGroupInterface

    return data
  }

  private generateVariantsJsonLd(
    product: MatrixProduct,
    swatchMapping?: KeyValueInterface,
    resolveAttributes?: KeyValueInterface
  ): StructuredDataProductVariantInterface[] {

    const flatProducts = product.getFlatItems(
      swatchMapping ? Object.keys(swatchMapping) : undefined,
      resolveAttributes ? Object.keys(resolveAttributes) : undefined
    )

    let variantsJson: StructuredDataProductVariantInterface[] = []

    if (! flatProducts || ! flatProducts.length) return variantsJson

    for (const flatProduct of flatProducts) {
      const v = this.generateVariantJsonLd(flatProduct, swatchMapping, resolveAttributes)
      if (v) variantsJson.push(v)
    }

    return variantsJson
  }

  private renderImageUrl(slug: string, width?: number) {
    return this.envService.config.imagesURL + "/" + slug + ( width ? '-' + width : '') + '.webp';
  }

  private generateVariantJsonLd(
    flatProduct: FlatProductItemInterface,
    swatchMapping?: KeyValueInterface,
    resolveAttributes?: KeyValueInterface
  ): StructuredDataProductVariantInterface | undefined {

    // Only the properties that actually differ between the variants belong here, everything else
    // (description, brand, material, ...) is inherited from the surrounding ProductGroup.
    let data: StructuredDataProductVariantInterface = {
      "@type": "Product",
      name: flatProduct.name,
      image: this.renderImageUrl(flatProduct.image),
      sku: flatProduct.id,
      mpn: flatProduct.id,
      url: flatProduct.url
    }

    // map values from our attributeCodes to Schema.org Properties
    if (swatchMapping) {
      Object.entries(swatchMapping).forEach(([ myKey, schemaOrgKey ]) => {
        if (flatProduct.attributes[myKey]) {
          data[schemaOrgKey] = flatProduct.attributes[myKey] as string
        }
      })
    }

    if (resolveAttributes) {
      Object.entries(resolveAttributes).forEach(([ myKey, schemaOrgKey ]) => {
        if (flatProduct.attributes[myKey]) {
          data[schemaOrgKey] = flatProduct.attributes[myKey] as string
        }
      })
    }

    data.offers = this.generateProductOffersJsonLd(flatProduct)

    return data
  }

  private generateBrandJsonLd(b: string): StructuredDataBrandInterface {
    return {
      "@type": "Brand",
      name: b
    }
  }

  private generateProductOffersJsonLd(
    flatProduct: FlatProductItemInterface,
  ): StructuredDataProductOfferInterface {
    
    // The active price has to sit on the offer itself, a nested priceSpecification alone is
    // reported as a missing price. Google ignores the priceSpecification as soon as a price is
    // given here, so it is only used to mark the struck through price of a running discount.
    // @see: https://developers.google.com/search/docs/appearance/structured-data/merchant-listing
    let data: StructuredDataProductOfferInterface = {
      "@type": "Offer",
      "price": flatProduct.specialPrice ? flatProduct.specialPrice : flatProduct.price,
      "priceCurrency": "EUR",
      "availability": flatProduct.inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
      // @TODO: split this, when furniture data will be rendered
      // seller: {
      //   "@type": "Organization",
      //   name: product.isFurniture() ? "GEA" : "Waldviertler"
      // },
      seller: { "@id": '#gea-organization' },
      "hasMerchantReturnPolicy": { "@id": "#return_policy" }
    }

    if (flatProduct.specialPrice && flatProduct.specialPrice < flatProduct.price) {
      const priceSpecification: StructuredDataProductPriceSpecificationInterface = {
        "@type": "UnitPriceSpecification",
        "priceType": "https://schema.org/StrikethroughPrice",
        "price": flatProduct.price,
        "priceCurrency": "EUR",
        "valueAddedTaxIncluded": true
      }
      data.priceSpecification = priceSpecification

      // A date in the past would keep the snippet from being displayed at all
      if (flatProduct.specialPriceValidTo && flatProduct.specialPriceValidTo.getTime() > Date.now()) {
        data.priceValidUntil = flatProduct.specialPriceValidTo.getISO8601String()
      }
    }

    const shippingDetails = this.generateShippingDetailsJsonLd(flatProduct)
    if (shippingDetails.length) {
      data.shippingDetails = shippingDetails
    }

    return data
  }

  /**
   * Builds the shipping details of a single offer and returns them as @id references.
   *
   * All countries sharing the same rate are merged into one OfferShippingDetails, and identical
   * details are shared between all offers of the product instead of being repeated.
   */
  private generateShippingDetailsJsonLd(
    flatProduct: FlatProductItemInterface,
  ): StructuredDataReferenceInterface[] {
    if (! flatProduct.minShippingCosts) return []

    // group the countries by the costs they share
    let countriesByRate: Map<number, string[]> = new Map()
    for (let [ countryCode, shippingCosts ] of flatProduct.minShippingCosts) {
      const countries = countriesByRate.get(shippingCosts)
      if (countries) {
        countries.push(countryCode)
      } else {
        countriesByRate.set(shippingCosts, [ countryCode ])
      }
    }

    let references: StructuredDataReferenceInterface[] = []

    for (let [ shippingCosts, countryCodes ] of countriesByRate) {
      // sorted, so equal groups always produce the same signature
      const shippingDestination: StructuredDataDefinedRegionInterface[] = countryCodes.sort().map(countryCode => {
        return {
          "@type": "DefinedRegion",
          "addressCountry": countryCode
        }
      })

      references.push({ "@id": this.registerShippingDetailsJsonLd({
        "@type": "OfferShippingDetails",
        "shippingRate": {
          "@type": "MonetaryAmount",
          "value": shippingCosts,
          "currency": "EUR"
        },
        shippingDestination,
        deliveryTime: {
          "@type": "ShippingDeliveryTime",
          handlingTime: {
            "@type": "QuantitativeValue",
            minValue: 1,
            maxValue: 2,
            unitCode: "DAY"
          },
          transitTime: {
            "@type": "QuantitativeValue",
            minValue: flatProduct.deliveryDaysFrom,
            maxValue: flatProduct.deliveryDaysTo,
            unitCode: "DAY"
          }
        }
      })})
    }

    return references
  }

  /**
   * Keeps every distinct shipping details node exactly once and returns the @id to reference it by.
   */
  private registerShippingDetailsJsonLd(item: StructuredDataProductShippingDetailsInterface): string {
    const signature = JSON.stringify(item)

    const knownId = this.shippingDetailIds.get(signature)
    if (knownId) return knownId

    const id = '#shipping-details-' + (this.shippingDetailIds.size + 1)
    this.shippingDetailIds.set(signature, id)
    this.shippingDetails.push({ "@context": "https://schema.org", "@id": id, ... item })

    return id
  }
}
